import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Typography,
  TableSortLabel,
  TablePagination,
} from "@mui/material";
import { QueueOrderSearchResult } from "../../../../../../../models/queue/queueOrderSearchResponse";
import { getStatusChipProps, getStatusLabel } from "../utils/statusUtils";
import { colors } from "../../../../../../../common/theme";
import { formatDateWithoutTimezone } from "../../../../../../../core/utils";
import Styles from "../queueOrdersReport.style";

interface OrdersTableProps {
  orders: QueueOrderSearchResult[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  sortBy: string;
  sortDirection: "asc" | "desc";
  isLoading: boolean;
  onSort: (column: string) => void;
  onPageChange: (event: unknown, newPage: number) => void;
  onPageSizeChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}

export const OrdersTable: React.FC<OrdersTableProps> = ({
  orders,
  totalCount,
  pageNumber,
  pageSize,
  sortBy,
  sortDirection,
  isLoading,
  onSort,
  onPageChange,
  onPageSizeChange,
}) => {
  return (
    <TableContainer component={Paper} sx={Styles.tableContainer}>
      <Table sx={{ tableLayout: 'fixed' }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...Styles.tableHeader, width: '12%' }}>
              <TableSortLabel
                active={sortBy === "OrderNo"}
                direction={sortBy === "OrderNo" ? sortDirection : "asc"}
                onClick={() => onSort("OrderNo")}
                sx={Styles.tableSortLabel}
              >
                Order Number
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ ...Styles.tableHeader, width: '12%' }}>
              <TableSortLabel
                active={sortBy === "OrderStatus"}
                direction={sortBy === "OrderStatus" ? sortDirection : "asc"}
                onClick={() => onSort("OrderStatus")}
                sx={Styles.tableSortLabel}
              >
                Order Status
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ ...Styles.tableHeader, width: '12%' }}>Done By</TableCell>
            <TableCell sx={{ ...Styles.tableHeader, width: '12%' }}>
              <TableSortLabel
                active={sortBy === "ActionDate"}
                direction={sortBy === "ActionDate" ? sortDirection : "asc"}
                onClick={() => onSort("ActionDate")}
                sx={Styles.tableSortLabel}
              >
                Action Date
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ ...Styles.tableHeader, width: '12%' }}>
              <TableSortLabel
                active={sortBy === "OrderDelay"}
                direction={sortBy === "OrderDelay" ? sortDirection : "asc"}
                onClick={() => onSort("OrderDelay")}
                sx={Styles.tableSortLabel}
              >
                Order Delay (Days)
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ ...Styles.tableHeader, width: '40%' }}>Filter Values</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {orders.map((order, index) => (
            <TableRow
              key={`${order.orderNo}-${index}`}
              sx={Styles.tableRow}
            >
              <TableCell>
                <Typography sx={Styles.orderNo}>{order.orderNo}</Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label={getStatusLabel(order.orderStatus)}
                  size="small"
                  {...getStatusChipProps(order.orderStatus)}
                />
              </TableCell>
              <TableCell>{order.doneBy || "-"}</TableCell>
              <TableCell>
                {order.actionDate ? formatDateWithoutTimezone(order.actionDate) : "-"}
              </TableCell>
              <TableCell sx={{ color: order.orderDelay > 0 ? colors.error["500"] : "inherit" }}>
                {order.orderDelay === null || order.orderDelay <= 0 ? "-" : order.orderDelay}
              </TableCell>
              <TableCell>{order.filterValues || "-"}</TableCell>
            </TableRow>
          ))}
          {orders.length === 0 && !isLoading && (
            <TableRow>
              <TableCell colSpan={6} align="center">
                <Typography color="textSecondary">
                  No orders found
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <TablePagination
        component="div"
        count={totalCount}
        page={pageNumber - 1} // Convert from 1-based to 0-based for TablePagination
        onPageChange={onPageChange}
        rowsPerPage={pageSize}
        onRowsPerPageChange={onPageSizeChange}
        rowsPerPageOptions={[10, 25, 50]}
        labelRowsPerPage="Items per page:"
        showFirstButton
        showLastButton
        SelectProps={{
          disabled: isLoading,
        }}
        sx={Styles.pagination}
      />
    </TableContainer>
  );
};
